import {
  DefaultTeamMemberData,
  PartnerCenterSeller,
  TeamDetailResponse,
  TICKET_PAGE_SIZE,
  TicketCategory,
  TicketData,
  TicketDetailData,
  TicketingAccessMode,
  TicketPaginationState,
  TicketSubCategory,
  TicketTeamAdminData,
  TicketTeamData,
  TranslationMap,
} from "../../models/ticketing";
import { OnboardingRootState } from "../reducers/rootReducer";
import { getOnboardingState } from "./rootSelectors";

export const getTicketingIsVerified = (state: OnboardingRootState): boolean =>
  getOnboardingState(state)?.ticketing?.isVerified ?? false;

export const getTicketingIsVerifying = (state: OnboardingRootState): boolean =>
  getOnboardingState(state)?.ticketing?.isVerifying ?? false;

export const getTicketingIsVerifyChecked = (state: OnboardingRootState): boolean =>
  getOnboardingState(state)?.ticketing?.isVerifyChecked ?? false;

export const getTicketingVerifyError = (state: OnboardingRootState): boolean =>
  getOnboardingState(state)?.ticketing?.verifyError ?? false;

export const getTicketingAccessMode = (state: OnboardingRootState): TicketingAccessMode =>
  getOnboardingState(state)?.ticketing?.accessMode ?? TicketingAccessMode.Denied;

export const getTicketingSellers = (state: OnboardingRootState): PartnerCenterSeller[] =>
  getOnboardingState(state)?.ticketing?.sellers ?? [];

export const getTicketingSelectedAccessId = (state: OnboardingRootState): string | undefined =>
  getOnboardingState(state)?.ticketing?.selectedAccessId;

export const getTicketingTickets = (state: OnboardingRootState): TicketData[] =>
  getOnboardingState(state)?.ticketing?.tickets ?? [];

export const getTicketingIsTicketsFetched = (state: OnboardingRootState): boolean =>
  getOnboardingState(state)?.ticketing?.isTicketsFetched ?? false;

export const getTicketingCategories = (state: OnboardingRootState): TicketCategory[] =>
  getOnboardingState(state)?.ticketing?.categories ?? [];

export const getTicketingSubCategories = (state: OnboardingRootState): TicketSubCategory[] =>
  getOnboardingState(state)?.ticketing?.subCategories ?? [];

export const getTicketingIsOptionsFetched = (state: OnboardingRootState): boolean =>
  getOnboardingState(state)?.ticketing?.isOptionsFetched ?? false;

export const getTicketingIsSubmitting = (state: OnboardingRootState): boolean =>
  getOnboardingState(state)?.ticketing?.isSubmitting ?? false;

export const getTicketingSubmitSuccess = (state: OnboardingRootState): boolean =>
  getOnboardingState(state)?.ticketing?.submitSuccess ?? false;

export const getTicketingSubmitError = (state: OnboardingRootState): string | undefined =>
  getOnboardingState(state)?.ticketing?.submitError;

export const getTicketingSelectedTicket = (
  state: OnboardingRootState
): TicketDetailData | undefined => getOnboardingState(state)?.ticketing?.selectedTicket;

export const getTicketingIsDetailLoading = (state: OnboardingRootState): boolean =>
  getOnboardingState(state)?.ticketing?.isDetailLoading ?? false;

export const getTicketingIsAddingComment = (state: OnboardingRootState): boolean =>
  getOnboardingState(state)?.ticketing?.isAddingComment ?? false;

export const getTicketingIsUpdating = (state: OnboardingRootState): boolean =>
  getOnboardingState(state)?.ticketing?.isUpdating ?? false;

export const getTicketingNotificationEmail = (state: OnboardingRootState): string | undefined =>
  getOnboardingState(state)?.ticketing?.notificationEmail;

export const getTicketingLoginEmail = (state: OnboardingRootState): string | undefined =>
  getOnboardingState(state)?.ticketing?.loginEmail;

export const getTicketingNotificationEmailOptOut = (state: OnboardingRootState): boolean =>
  getOnboardingState(state)?.ticketing?.notificationEmailOptOut ?? false;

export const getTicketingNotificationEmailVerified = (state: OnboardingRootState): boolean =>
  getOnboardingState(state)?.ticketing?.notificationEmailVerified ?? false;

export const getTicketingIsNotificationEmailSaving = (state: OnboardingRootState): boolean =>
  getOnboardingState(state)?.ticketing?.isNotificationEmailSaving ?? false;

export const getTicketingNotificationEmailError = (
  state: OnboardingRootState
): string | undefined => getOnboardingState(state)?.ticketing?.notificationEmailError;

export const getTicketingIsNewContact = (state: OnboardingRootState): boolean =>
  getOnboardingState(state)?.ticketing?.isNewContact ?? false;

export const getTicketingTranslationMap = (state: OnboardingRootState): TranslationMap =>
  getOnboardingState(state)?.ticketing?.translationMap ?? {};

const defaultPagination: TicketPaginationState = {
  currentPage: 1,
  totalPages: 0,
  totalCount: 0,
  pageSize: TICKET_PAGE_SIZE,
  sortBy: "createdon",
  sortDir: "desc",
  filterTypes: [],
  filterSeverities: [],
  search: "",
  queryTokens: {},
};

export const getTicketingPagination = (state: OnboardingRootState): TicketPaginationState =>
  getOnboardingState(state)?.ticketing?.pagination ?? defaultPagination;

export const getTicketingIsPaginatedFetching = (state: OnboardingRootState): boolean =>
  getOnboardingState(state)?.ticketing?.isPaginatedFetching ?? false;

export const getTicketingFetchGeneration = (state: OnboardingRootState): number =>
  getOnboardingState(state)?.ticketing?.fetchGeneration ?? 0;

export const getTicketingStatusCounts = (state: OnboardingRootState) =>
  getOnboardingState(state)?.ticketing?.statusCounts;

export const getTicketingTeams = (state: OnboardingRootState): TicketTeamData[] =>
  getOnboardingState(state)?.ticketing?.teams ?? [];

export const getTicketingTeamsAdmin = (state: OnboardingRootState): TicketTeamAdminData[] =>
  getOnboardingState(state)?.ticketing?.teamsAdmin ?? [];

export const getTicketingTeamsAdminSellerName = (state: OnboardingRootState): string => {
  const name = getOnboardingState(state)?.ticketing?.teamsAdminSellerName;
  return name && name !== "undefined" ? name : "";
};

export const getTicketingIsTeamsAdminFetching = (state: OnboardingRootState): boolean =>
  getOnboardingState(state)?.ticketing?.isTeamsAdminFetching ?? false;

export const getTicketingTeamsAdminError = (state: OnboardingRootState): string | undefined =>
  getOnboardingState(state)?.ticketing?.teamsAdminError;

export const getIsTicketingAdmin = (state: OnboardingRootState): boolean | undefined =>
  getOnboardingState(state)?.ticketing?.isTicketingAdmin;

export const getIsTicketingManager = (state: OnboardingRootState): boolean | undefined =>
  getOnboardingState(state)?.ticketing?.isTicketingManager;

export const getTicketingDefaultTeamMembers = (
  state: OnboardingRootState
): DefaultTeamMemberData[] => getOnboardingState(state)?.ticketing?.defaultTeamMembers ?? [];

export const getTicketingIsDefaultTeamMembersFetching = (state: OnboardingRootState): boolean =>
  getOnboardingState(state)?.ticketing?.isDefaultTeamMembersFetching ?? false;

export const getTicketingIsCreatingTeam = (state: OnboardingRootState): boolean =>
  getOnboardingState(state)?.ticketing?.isCreatingTeam ?? false;

export const getTicketingCreateTeamError = (state: OnboardingRootState): string | undefined =>
  getOnboardingState(state)?.ticketing?.createTeamError;

export const getTicketingCreatedTeamId = (state: OnboardingRootState): string | undefined =>
  getOnboardingState(state)?.ticketing?.createdTeamId;

export const getTicketingTeamDetail = (
  state: OnboardingRootState
): TeamDetailResponse | undefined => getOnboardingState(state)?.ticketing?.teamDetail;

export const getTicketingIsTeamDetailFetching = (state: OnboardingRootState): boolean =>
  getOnboardingState(state)?.ticketing?.isTeamDetailFetching ?? false;

export const getTicketingTeamDetailError = (state: OnboardingRootState): string | undefined =>
  getOnboardingState(state)?.ticketing?.teamDetailError;

export const getTicketingIsRemovingMember = (state: OnboardingRootState): boolean =>
  getOnboardingState(state)?.ticketing?.isRemovingMember ?? false;

export const getTicketingIsAddingMembers = (state: OnboardingRootState): boolean =>
  getOnboardingState(state)?.ticketing?.isAddingMembers ?? false;

export const getTicketingIsRenamingTeam = (state: OnboardingRootState): boolean =>
  getOnboardingState(state)?.ticketing?.isRenamingTeam ?? false;

export const getTicketingIsRemovingManager = (state: OnboardingRootState): boolean =>
  getOnboardingState(state)?.ticketing?.isRemovingManager ?? false;
